import React, { ReactElement } from 'react';
import { Inject } from '@odinjs/odin';
import moment from 'moment';

import { Card, DateFieldAPI, FilterAPI, ModalButton, ModalProps, ModalService, PanelAPI, Text, TimelineAPI, TimelineEvent } from '@philips-internal/oswald-ui-components';
import Controller, { OnBeforeSubmit, OnChange, OnDynamicContentRender, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableCloseEvent, AbortableSubmitEvent, MountEvent, RenderEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { ModalButtonType, ModalType, SelectionType } from '@philips-internal/oswald-ui-constants';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import { Value } from '@philips-internal/oswald-ui-core';

import BaseController from '../../../BaseController';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { ApplicationRoute } from '../../../../constants/routes';
import { DateFilterUtils } from '../../../../utils/date-filter-utils';
import { MedicineAndSolutionsParams } from '../medicine-and-solutions/medicine-and-solutions-params';
import { ElectronicServiceTimelineAliases, FragmentNames } from '../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { YesNoType } from '../../../../utils/enum-utils';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../utils/parameters';
import TimelineContext, { PrescriptionType, TimeLineEventMedication, TimelineEventSolution } from '../../services/timeline-service';
import { FilterParam, TimelineParams } from './electronic-service-timeline-params';
import { legends } from './legends';
import TimelineService from './timeline-service';
import {  SimpleCard } from './cards/simple-card';

import './electronic-service-timeline-controller.css';
import { TimelineCpoeMaterialResponse } from './electronic-service-timeline-response';
import { DateFormatterUtil } from '@philips-emr/health-professional-components';

@Controller({ component: ElectronicServiceTimelineAliases.EMAR_TIMELINE_LAYOUT, fragment: FragmentNames.ELECTRONIC_SERVICE_TIMELINE })
class ElectronicServiceTimelineController extends BaseController {

  @Inject(ElectronicServiceTimelineAliases.BEDSIDE_TIMELINE)
  private timeLineAPI!: TimelineAPI;

  @Inject(ElectronicServiceTimelineAliases.RESERVED_BLOOD_PRODUCTS_PANEL)
  private reservedPanelApi!: PanelAPI;

  private prescriptionDateWithDefaulValue = true;
  private internalNavigation!: boolean;

  private initFilter(timelineFilter: FilterAPI): void {

    const defaultPreviousHours = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, 'DEFAULT_PREVIOUS_HOURS_MANAGEMENT_TAB_FILTER#94');
    let defaultPrescriptionHour = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, 'FIRST_FILTER_TIME#151');
    const useCurrentDate = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, 'USE_CURRENT_DATE_AS_FILTER#542');
    let currentDate = DateFilterUtils.getCurrentDate();

    if (defaultPrescriptionHour === '00:00:00' && 'N' !== useCurrentDate) {
        if ('S' === useCurrentDate) {
          defaultPrescriptionHour = moment().format('LT');
        } else {
          const dateFilter = moment().subtract(Number(defaultPreviousHours), 'hours');
          currentDate = dateFilter.format(ISO_DATE_FORMAT);
          defaultPrescriptionHour = dateFilter.format('LT');
          this.prescriptionDateWithDefaulValue = false;
        }
        defaultPrescriptionHour = defaultPrescriptionHour.concat(':00');
    }

    const record = {
      additionalHoursQuantity: this.getAdditionalHours(),
      performedItems: this.getPerformedItemsDefaultValue(),
      prescriptionDate: currentDate,
      prescriptionHour: DateFormatterUtil.timeFieldToTimezone(defaultPrescriptionHour ? defaultPrescriptionHour.toString() : '00:00:00') + 'Z',
      previousHoursQuantity: defaultPreviousHours as number,
    };

    const restored = timelineFilter.restoreState(true);

    if (restored) {
      const recordSource = timelineFilter.getRecord();

      if (Object.keys(recordSource).length === 0) {
        record.prescriptionDate = currentDate;
      }

      Object.assign(record, timelineFilter.getRecord());
      this.prescriptionDateWithDefaulValue = false;
    }

    timelineFilter.setData(record);
    timelineFilter.open();
    timelineFilter.submit();
  }

  getAdditionalHours(): number {
    let defaultAdditionalHours = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.DEFAULT_ADDITIONAL_HOURS) as number;
    if(!defaultAdditionalHours || isNaN(defaultAdditionalHours) || defaultAdditionalHours > 72 || defaultAdditionalHours < 0) {
      defaultAdditionalHours = 72;
    }
    return defaultAdditionalHours;
  }

  getPerformedItemsDefaultValue(): boolean {
    return FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.DEFAULT_PERFORMED_ITEMS) as string === YesNoType.YES;
  }

  handleParamTasyTwsParam(parameterTwsOrTasy: string): string {
    return FoundationUtils.findModuleParam(FunctionName.ADEP_1113, parameterTwsOrTasy) as string;
  }

  modalSecurityAlertIsVisible(patientId: string, encounterId: string): void {
    const adepNumberFunction = 1113;

    const dialogModal: ModalProps = {
      closeByDocument: false,
      closeByEscape: false,
      contextData: {
        alertPersonCode: patientId,
        encounterNumber: encounterId,
        functionCode: adepNumberFunction,
      },
      focusOnOpen: false,
      fragment: 'security-alert',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'tws.information-bar.alerts',
      type: ModalType.FULLSCREEN,
      uuid: generateUUID(),
    };

    ModalService.openFullscreen(dialogModal);
  }

  dialogModalConfirm(event: AbortableCloseEvent): void {
    event.abort();

    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => { },
      title: 'tws.biometry.cancel',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => ModalService.close('medication-administration-by-process'),
      title: 'tws.file-uploader-modal.confirm',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: ModalProps = {
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: MessageService.translate('text.reset items-want-to-continue'),
      dockButtons: buttons,
      focusOnOpen: true,
      title: 'text.message-alert-attention',
      uuid: generateUUID(),
    };

    ModalService.openDialog(dialogModal);
  }

  modalAdministrationByProcessIsVisible(processNumber: string): void {
    const titleDescription = MessageService.translate('text.medication-of-process').replace('#@NR_PROCESSO#@', processNumber);

    const dialogModal: ModalProps = {
      closeByDocument: false,
      closeByEscape: false,
      contextData: {
        medicationId: processNumber,
      },
      focusOnOpen: false,
      fragment: 'medication-administration-by-process',
      id: 'medication-administration-by-process',
      onBeforeClose: this.dialogModalConfirm,
      title: titleDescription,
      type: ModalType.FULLSCREEN,
      uuid: generateUUID(),
    };

    ModalService.openFullscreen(dialogModal);
  }

  @OnMount(ElectronicServiceTimelineAliases.EMAR_TIMELINE_LAYOUT)
  onEmarTimelineLayoutMount(): void {
    InformationBarUtils.collapsedBar = true;
    this.internalNavigation = false;

    const tasyParam = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.VIEW_PATIENT_ALERTS);
    const modalAlertIsVisible = MedicineAndSolutionsParams.params?.modalIsVisible;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const adepNumberFunction = 1113;

    this.context.set({
      alertPersonCode: patientId,
      encounterId,
      encounterNumber: encounterId,
      functionCode: adepNumberFunction,
      patientId
    });

    if (modalAlertIsVisible === YesNoType.YES && tasyParam && tasyParam === YesNoType.YES) {
      TimelineService.getSecurityAlertList(String(patientId), String(encounterId)).then(res => {
        const alertsList = res.body as [];

        if (alertsList.length > 0) {
          this.modalSecurityAlertIsVisible(String(patientId), String(encounterId));
        }
      });
    }
  }

  @OnMount(ElectronicServiceTimelineAliases.TIMELINE_FILTER)
  onTimelineMoun(event: MountEvent): void {
    const filter = event.target as FilterAPI;
    this.prescriptionDateWithDefaulValue = true;

    this.initFilter(filter);
  }

  @OnUnmount(ElectronicServiceTimelineAliases.TIMELINE_FILTER)
  onTimelineFilterUnmount(event: MountEvent): void {
    const filter = event.target as FilterAPI;

    if (this.internalNavigation) {

      const record = filter.getRecord() as Record<string, FilterParam>;

      const data = Object.keys(record).length === 0
        ? { prescriptionDate: DateFilterUtils.getCurrentDate() }
        : record;

      filter.setData(data);
      filter.persistState();

    } else {

      filter.clearPersistedState();

    }
  }

  getPrescriptionDate(record: Record<string, Value>): string {
    const inputDateValue = this.prescriptionDateWithDefaulValue ? DateFilterUtils.getCurrentDate() : record.prescriptionDate;
    const inputHourValue = record.prescriptionHour;

    let prescriptionDate: string;

    if (inputDateValue && inputHourValue) {
      const date = String(inputDateValue).includes('T') ? String(inputDateValue).split('T')[0] : inputDateValue;
      const hour = String(inputHourValue).includes('T') ? String(inputHourValue).split('T')[1] : inputHourValue;

      prescriptionDate = moment(`${String(date)} ${hour}`).format(ISO_FULL_DATE_FORMAT);
    } else {
      prescriptionDate = moment().format(ISO_FULL_DATE_FORMAT);
    }

    return prescriptionDate;
  }

  @OnChange(ElectronicServiceTimelineAliases.PRESCRIPTION_DATE_DATE_FIELD)
  onChangePrescripionDateField(): void {
    this.prescriptionDateWithDefaulValue = false;
  }

  @OnBeforeSubmit(ElectronicServiceTimelineAliases.TIMELINE_FILTER)
  onBeforeSubmit(event: AbortableSubmitEvent): void {
    if (this.shouldAbort(event)) {
      event.abort();
      return;
    }
    this.reservedPanelApi.setVisible(false);
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const userSectorProcedureTypeParam = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, 'USER_SECTOR_PROCEDURE_TYPE#64');
    const userSectorIntervalTypeParam = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, 'USER_SECTOR_INTERVAL_TYPE#72');
    const modelSequenceNumberParam = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, 'MODEL_SEQUENCE_NUMBER#2');
    const timeLineParams: TimelineParams = {
      additionalHoursQuantity: event.record.additionalHoursQuantity,
      encounterNumber: Number(encounterId),
      modelSequenceNumber: modelSequenceNumberParam ? modelSequenceNumberParam.valueOf() as number : 0,
      performedItems: event.record.performedItems,
      prescriptionDate: this.getPrescriptionDate(event.record),
      prescriptionHourQuantity: 6,
      previousHoursQuantity: event.record.previousHoursQuantity,
      userSectorIntervalType: userSectorIntervalTypeParam ? userSectorIntervalTypeParam.valueOf() as string : YesNoType.NO,
      userSectorProcedureType: userSectorProcedureTypeParam ? userSectorProcedureTypeParam.valueOf() as string : YesNoType.NO,
    }

    const timelineAPI = this.timeLineAPI;
    TimelineService.getDataTimeLine(timeLineParams).then(data => {
      if (data && data.length > 0) {
        timelineAPI.setUngroupedTitle('tws.eletronic-service-personal-information.acm_sn-tab');
        timelineAPI.setData(data);
        timelineAPI.setFormatterByProperty('start', 'value', { DateFieldAPI });
      } else {
        timelineAPI.setData([]);
        timelineAPI.setUngroupedTitle('tws.timeline-others');
      }
    }).finally(() => {
      this.context.set({ performedItems: timeLineParams.performedItems });
      this.reservedPanelApi.setVisible(true)
    });

    if (this.prescriptionDateWithDefaulValue) {
      event.record['prescriptionDate'] = DateFilterUtils.getCurrentDate();
    }
  }

  shouldAbort(event: AbortableSubmitEvent): boolean {
    return !(event.record?.prescriptionHour && event.record?.prescriptionDate);
  }

  fillMedicationParametersToNextScreen(item: TimelineEvent): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    MedicineAndSolutionsParams.params = {
      cdIntervalId: item.intervalCode as string,
      cdMaterialId: item.itemCode as string,
      chemotherapy: item.chemotherapy as boolean,
      chemotherapyStatus: item.chemotherapyStatus as string,
      cpoeMaterial: item.cpoeMaterial as TimelineCpoeMaterialResponse,
      cpoeSequence: item.cpoeSequence as number,
      dsMaterialId: item.description,
      encounterId: String(encounterId),
      hourSequence: item.id as string,
      id: item.id,
      ieAcmId: item.pdoPrn ? YesNoType.YES : YesNoType.NO,
      ieSpecialTimeId: '',
      ieStatus: item.status as string,
      ieTypeItem: item.itemType as string,
      ieViaApplicationDescription: item.administrationRouteDescription as string,
      ieViaApplicationId: item.administrationRouteType as string,
      internalProcedureSequence: item.internalProcedureSequence as number,
      isMedication: item.isMedication as string,
      itemStatus: item.itemStatus as string,
      nrCurrentStageId: item.emarStepNumber as string,
      nrGroupingId: item.groupingNumber as string,
      nrPrescriptionId: item.prescriptionNumber as string,
      nrPrescriptionsId: item.prescriptionsNumber as string,
      nrSequenceId: item.itemSequence as string,
      observationDescription: item.observationDescription as string,
      patientId: String(patientId),
      pdoPrn: item.pdoPrn as boolean,
      qtDoseId: item.itemQuantity as string,
      startId: item.start as string,
      uom: item.uom as string,
      prescriptionValidityDate: item.prescriptionValidityDate as string
    }

    MedicineAndSolutionsParams.speedParam = {
      currentSpeedInfusion: item.currentSpeedInfusion as number,
    }
  }

  modalAdministrationByScheduleIsVisible(): void {
    const dialogModal: ModalProps = {
      closeByDocument: false,
      closeByEscape: false,
      contextData: {
        medicationId: '',
      },
      focusOnOpen: false,
      fragment: 'administration-by-schedule',
      id: 'administration-by-schedule',
      onBeforeClose: () => { },
      title: 'Administração',
      type: ModalType.FULLSCREEN,
      uuid: generateUUID(),
    };

    ModalService.openFullscreen(dialogModal);
  }

  private goToScheduleSupplements(item: TimelineEvent): void {
    const routeParams = {
      ...this.createRouteParams(item),
    }

    applicationContext.redirect(ApplicationRoute.ADEP_SCHEDULE_SUPPLEMENTS, routeParams);
  }

  gotToScheduleMedications = (item: TimelineEvent): void => {
    const { itemType } = item;
    let scheduleTypeId;

    switch (itemType) {
      case PrescriptionType.SOLUTION:
        scheduleTypeId = 'Solutions';
        break;
      case PrescriptionType.BLADDER_IRRIGATION:
        scheduleTypeId = 'BladderIrrigation';
        break;
      case PrescriptionType.MEDICINE:
      default:
        scheduleTypeId = 'Medications';
        break;
    }

    const routeParams = {
      ...this.createRouteParams(item),
      scheduleTypeId,
    }

    applicationContext.redirect(ApplicationRoute.ADEP_SCHEDULE_MEDICATIONS, routeParams);
  }

  getParamsToInfusionViewer = (item: TimelineEvent): Record<string, Value> => {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    TimelineContext.set(item as unknown as TimelineEventSolution);

    const prescriptionId = item.prescriptionNumber as number;
    const sequenceNumber = item.itemSequence as number;

    return {
      cpoeMaterial: item.cpoeMaterial as TimelineCpoeMaterialResponse,
      currentSpeedInfusion: String(item.currentSpeedInfusion),
      encounterId: String(encounterId),
      hourStatus: item.status,
      itemStatus: String(item.itemStatus),
      itemType: String(item?.itemType),
      patientId: String(patientId),
      prescriptionNumber: prescriptionId,
      scheduleSequenceNumber: item.id,
      sequenceNumber: sequenceNumber || 1,
      startDate: String(item.start),
    }
  }

  goToInfusionViewer = (item: TimelineEvent): void => {
    const routeParams = this.getParamsToInfusionViewer(item);
    TimelineContext.setCpoeMaterial(item.cpoeMaterial as TimelineCpoeMaterialResponse);
    applicationContext.redirect(ApplicationRoute.ADEP_INFUSION_VIEWER, routeParams);
  }

  goToMedicationViewer = (item: TimelineEvent): void => {
    const route = `${ApplicationRoute.ELECTRONIC_MEDICATION_ADMINISTRATION}${ApplicationRoute.PATIENT_LIST}${ApplicationRoute.ADEP_MEDICATION_VIEWER}`
    const routeParams = {
      ...this.createRouteParams(item),
      scheduleTypeId: 'Medications'
    }
    applicationContext.redirect(route, routeParams);
  }

  goToViewItem(item: TimelineEvent, router: ApplicationRoute): void {
    applicationContext.redirect(router, this.createRouteParams(item));
  }

  goToGastherapy = (item: TimelineEvent): void => {
    const routeParams = this.getParamsToInfusionViewer(item);
    applicationContext.redirect(ApplicationRoute.ADEP_GASTHERAPY, routeParams);
  }

  goToEnteralNutrition = (item: TimelineEvent): void => {
    const routeParams = this.getParamsToInfusionViewer(item);
    applicationContext.redirect(ApplicationRoute.ADEP_ENTERAL_NUTRITION, routeParams);
  }

  goToAdultTpn = (item: TimelineEvent): void => {
    const routeParams = this.getParamsToInfusionViewer(item);
    applicationContext.redirect(ApplicationRoute.ADEP_ADULT_TPN, routeParams);
  }

  goToNeonatalTpn = (item: TimelineEvent): void => {
    const routeParams = this.getParamsToInfusionViewer(item);
    applicationContext.redirect(ApplicationRoute.ADEP_NEONATAL_TPN, routeParams);
  }

  goToBloodManagement = (item: TimelineEvent): void => {
    const routeParams = this.getParamsToInfusionViewer(item);
    applicationContext.redirect(ApplicationRoute.ADEP_BLOOD_MANAGEMENT, routeParams);
  }

  goToNursingInterventions = (item: TimelineEvent): void => {
    const routeParams = this.getParamsToInfusionViewer(item);
    applicationContext.redirect(ApplicationRoute.ADEP_NURSING_INTERVENTIONS, routeParams);
  }

  private createRouteParams(item: TimelineEvent): Record<string, string | number> {
    TimelineContext.set(item as unknown as TimeLineEventMedication);

    return {
      encounterId: this.getHistoryRouteParam<number>('encounterId'),
      patientId: this.getHistoryRouteParam<number>('patientId'),
      scheduleSequenceNumber: item.id,
      status: item.status,
    }
  }

  private checkIfMedOrSupplement(item: TimelineEvent): void {
    const { itemType } = item;
    const allowSchedule = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_TO_SCHEDULE_ITEMS);
    if (allowSchedule === YesNoType.NO) {
      return;
    }
    if (itemType === PrescriptionType.ORAL_SUPPLEMENTS || itemType === PrescriptionType.ENTERAL_NUTRITION) {
      this.goToScheduleSupplements(item);
      return;
    }
    this.gotToScheduleMedications(item);
    return;
  }

  cardOnClick = (item: TimelineEvent): void => {
    this.internalNavigation = true;

    this.fillMedicationParametersToNextScreen(item);

    if (item.isPdoPrn || item.pdoPrn) {
      this.checkIfMedOrSupplement(item);
      return;
    }

    this.navigateToItemViewer(item)
  }

  navigateToItemViewer = (item: TimelineEvent): void => {
    switch (item.itemType) {
      case PrescriptionType.SOLUTION:
        this.goToInfusionViewer(item);
        break;
      case PrescriptionType.MEDICINE:
      case PrescriptionType.GLYCEMIA_CONTROLS_ASSOCIATED_ITEMS:
      case PrescriptionType.PROCEDURES_ASSOCIATED_ITEMS:
        this.goToMedicationViewer(item);
        break;
      case PrescriptionType.MILKS_AND_INFANT_FORMULAS:
        this.goToViewItem(item, ApplicationRoute.ADEP_MILK_AND_DERIVATIVES);
        break;
      case PrescriptionType.ORAL_DIET:
        this.goToViewItem(item, ApplicationRoute.ADEP_ORAL_DIET);
        break;
      case PrescriptionType.ORAL_SUPPLEMENTS:
        this.goToViewItem(item, ApplicationRoute.ADEP_ORAL_SUPPLEMENTS);
        break;
      case PrescriptionType.ENTERAL_NUTRITION:
        this.goToEnteralNutrition(item);
        break;
      case PrescriptionType.ADULT_PARENTERAL_NUTRITION:
        this.goToAdultTpn(item);
        break;
      case PrescriptionType.NEONATAL_PARENTERAL_NUTRITION:
      case PrescriptionType.NEONATAL_PARENTERAL_PEDIATRIC:
        this.goToNeonatalTpn(item);
        break;
      case PrescriptionType.BLOOD_MANAGEMENT:
        this.goToBloodManagement(item);
        break;
      case PrescriptionType.RECOMMENDATIONS:
        this.goToViewItem(item, ApplicationRoute.ADEP_RECOMMENDATION);
        break;
      case PrescriptionType.BLADDER_IRRIGATION:
        this.goToViewItem(item, ApplicationRoute.ADEP_BLADDER_IRRIGATION);
        break;
      case PrescriptionType.GASOTERAPY:
        this.goToGastherapy(item);
        break;
      case PrescriptionType.GLYCEMIC_CONTROL:
      case PrescriptionType.INTENCIVE_GLYCEMIC_CONTROL:
        this.goToViewItem(item, ApplicationRoute.ADEP_GLYCEMIC);
        break;
      case PrescriptionType.NURSING_INTERVENTIONS:
        this.goToNursingInterventions(item);
        break;
      default:
        break;
    }
  };

  private renderCard(item: TimelineEvent, renderCardContentByItemType: (item: TimelineEvent) => ReactElement): ReactElement {
    const {
      delayed
    } = item;

    return (
      <Card
        className='timeline-item'
        onClick={() => this.cardOnClick(item)}
        transparent={false}
        selectionType={SelectionType.SINGLE}
        superscription={legends}
        uuid={generateUUID()}
      >
        <div className={`timeline__main ${ delayed ? '--alert-border' : ''}`}>
          { renderCardContentByItemType(item) }
        </div>
      </Card>
    );
  }

  @OnDynamicContentRender(ElectronicServiceTimelineAliases.BEDSIDE_TIMELINE)
  onRenderInformationList({ data, entry, render, type }: RenderEvent): void {
    if (type === 'item') {
      render(() => this.renderCard(data as unknown as TimelineEvent, this.renderCardContentByItemType));
    }

    if (type === 'groupHeader') {
      render(() => this.renderGroupHeader(entry));
    }
  }

  private renderGroupHeader(entry: string): ReactElement {
    return (
      <div className='timeline-group-header'>
        <Text className='timeline-group-header__title'>{entry}</Text>
      </div>
    );
  }

  renderCardContentByItemType = (item: TimelineEvent): ReactElement => {
    return SimpleCard(item);
  }
}

export default ElectronicServiceTimelineController;
